/**
 * Ficha do pneu — responde: qual é, onde está (veículo, eixo, posição), desde
 * quando e com qual KM, quanto rodou, sulco, vida, recapagens, inspeções,
 * onde já esteve, movimentações, problemas e quanto custou. Cada número
 * credita a origem (SIAG × Finatto/Sinersoft) onde isso muda a leitura.
 */
import { useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { TireDetailDto, TireTimelineItemDto } from "@bi/types";
import { api, ApiError } from "../lib/api";
import { fmtBRL } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { IconTire, IconTruck } from "../components/icons";
import { MoveTireModal, type MoveTirePreset } from "../features/tires/MoveTireModal";
import { InspectTireModal } from "../features/tires/InspectTireModal";
import {
  ConditionBadge, fmtDate, fmtDateTime, fmtKm, fmtMm, fmtPsi, lifeText, OriginBadge, RETREAD_STATUS, TabBar, TireStatusBadge,
  useTireCapabilities,
} from "../features/tires/tire-ui";

type Aba = "visao" | "historico" | "inspecoes" | "movimentacoes" | "recapagens" | "custos";

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <div className="text-sm font-medium text-gray-900 mt-0.5">{children}</div>
    </div>
  );
}

function Stat({ label, value, sub, wrap }: { label: string; value: ReactNode; sub?: ReactNode; wrap?: boolean }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-2 font-bold text-gray-900 ${wrap ? "text-base leading-snug" : "text-xl truncate"}`}>{value}</p>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

export function TireDetailPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const aba = (params.get("aba") as Aba) || "visao";
  const { can } = useTireCapabilities();
  const [mover, setMover] = useState<MoveTirePreset | null>(null);
  const [inspecionar, setInspecionar] = useState(false);

  const q = useQuery({ queryKey: ["tires", "detail", id], queryFn: () => api.get<TireDetailDto>(`/fleet/tires/${id}`), retry: false });
  const hist = useQuery({
    queryKey: ["tires", "history", id],
    queryFn: () => api.get<{ items: TireTimelineItemDto[] }>(`/fleet/tires/${id}/history`),
    enabled: aba === "historico" || aba === "movimentacoes",
  });

  if (q.isLoading) return <div className="card p-6 text-sm text-gray-500">Carregando pneu…</div>;
  if (q.isError || !q.data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Pneu" />
        <EmptyState icon={<IconTire className="w-6 h-6" />} title="Pneu não encontrado"
          description={q.error instanceof ApiError && q.error.status !== 404 ? q.error.message : "Ele não existe ou não pertence a esta empresa."}
          action={<Link className="btn-secondary" to="/frota/pneus?aba=pneus">Voltar à lista</Link>} />
      </div>
    );
  }

  const d = q.data;
  const t = d.tire;
  const loc = t.location;
  const vidaPct = t.treadMm != null && t.mmNew != null && t.mmSafety != null && t.mmNew > t.mmSafety
    ? Math.max(0, Math.min(1, (t.treadMm - t.mmSafety) / (t.mmNew - t.mmSafety)))
    : null;
  const setAba = (a: Aba) => {
    const p = new URLSearchParams(params);
    if (a === "visao") p.delete("aba"); else p.set("aba", a);
    setParams(p, { replace: true });
  };
  const problemas = d.inspections.filter((i) => i.problems.length > 0 || i.condition !== "normal");

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Pneu ${t.code}`}
        subtitle={[t.brand, t.model, t.size].filter(Boolean).join(" · ") || undefined}
        badge={
          <div className="flex gap-1.5 flex-wrap">
            <TireStatusBadge status={t.status} />
            <span className="badge-gray">{lifeText(t.life)}</span>
            {t.condition && t.condition !== "normal" && t.status !== "written_off" && <ConditionBadge condition={t.condition} />}
          </div>
        }
        actions={
          <div className="flex gap-2">
            <Link className="btn-ghost" to="/frota/pneus?aba=pneus">Lista</Link>
            {can("tires.inspect") && t.status !== "written_off" && <button className="btn-secondary" onClick={() => setInspecionar(true)}>Inspecionar</button>}
            {can("tires.move") && d.allowedActions.length > 0 && <button className="btn-primary" onClick={() => setMover({ tireId: t.id })}>Movimentar pneu</button>}
          </div>
        }
      />

      {!d.managedBySiag && (
        <p className="text-sm text-gray-600 bg-surface-muted rounded-lg px-4 py-2">
          A situação deste pneu ainda vem da integração (<OriginBadge origin={t.source} />). Ao registrar a primeira movimentação no SIAG, o histórico físico passa a ser controlado aqui.
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat label="Veículo atual" value={loc ? <Link className="hover:underline font-mono" to={`/frota/placa/${encodeURIComponent(loc.plate)}?aba=pneus`}>{loc.plate}</Link> : "—"}
          sub={loc ? <OriginBadge origin={loc.origin === "siag" ? "siag" : t.source} /> : t.statusLabel} />
        <Stat label="Posição atual" wrap value={loc?.positionLabel ?? "—"} sub={loc?.installedAt ? `desde ${fmtDate(loc.installedAt)}` : undefined} />
        <Stat label="Sulco atual" value={fmtMm(t.treadMm)} sub={t.treadOrigin ? <>fonte <OriginBadge origin={t.treadOrigin} />{t.treadAt ? ` · ${fmtDate(t.treadAt)}` : ""}</> : "sem medição"} />
        <Stat label="KM acumulado" value={fmtKm(d.km.accumulated)} sub={d.km.siagInstallations > 0 ? `${fmtKm(d.km.siagInstallations)} rodados no SIAG` : d.km.accumulatedOrigin ? <OriginBadge origin={d.km.accumulatedOrigin} /> : undefined} />
        <Stat label="Custo acumulado" value={fmtBRL(d.costs.total)} sub={d.costs.costPerKm != null ? `R$ ${d.costs.costPerKm.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}/km` : undefined} />
        <Stat label="Recapagens" value={d.retreadCount.total} sub={d.retreadCount.siag > 0 ? `${d.retreadCount.integration} da integração + ${d.retreadCount.siag} no SIAG` : undefined} />
      </div>

      <TabBar<Aba>
        active={aba}
        onChange={setAba}
        tabs={[
          { key: "visao", label: "Visão Geral" },
          { key: "historico", label: "Histórico" },
          { key: "inspecoes", label: "Inspeções", count: d.inspections.length },
          { key: "movimentacoes", label: "Movimentações" },
          { key: "recapagens", label: "Recapagens", count: d.retreads.length },
          { key: "custos", label: "Custos" },
        ]}
      />

      {aba === "visao" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card p-5 flex gap-5">
            <div className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-full bg-gray-900 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-gray-300 border-4 border-gray-500" />
              </div>
              <TireStatusBadge status={t.status} />
            </div>
            <div className="grid grid-cols-1 gap-3 flex-1 min-w-0">
              <Info label="Código interno">{t.code} <span className="text-xs text-gray-400">· #{t.externalTireId} <OriginBadge origin={t.source} /></span></Info>
              <Info label="Nº de fogo">{t.fireNumber ?? "—"}</Info>
              <Info label="Marca / modelo">{[t.brand, t.model].filter(Boolean).join(" · ") || "—"}</Info>
              <Info label="Medida">{t.size ?? "—"}</Info>
              <div className="grid grid-cols-2 gap-3">
                <Info label="Vida atual">{lifeText(t.life)}</Info>
                <Info label="Data de compra">{fmtDate(d.purchaseDate)}</Info>
              </div>
              <Info label="Valor de compra">{t.purchaseValue > 0 ? fmtBRL(t.purchaseValue) : "—"}</Info>
              {t.externalStatus && <Info label="Status na Finatto">{t.externalStatus.label}</Info>}
            </div>
          </div>
          <div className="card p-5 space-y-4">
            <p className="text-sm font-semibold text-gray-900">Localização atual</p>
            {loc ? (
              <div className="flex items-center gap-4 rounded-lg border border-surface-border p-3">
                <IconTruck className="w-8 h-8 text-gray-400" />
                <div className="grid grid-cols-2 gap-3 flex-1">
                  <Info label="Veículo"><span className="font-mono">{loc.plate}</span></Info>
                  <Info label="Posição">{loc.positionLabel ?? "—"}</Info>
                  <Info label="Instalado em">{fmtDate(loc.installedAt)}</Info>
                  <Info label="KM na instalação">{fmtKm(loc.installedOdometerKm)}</Info>
                </div>
              </div>
            ) : <p className="text-sm text-gray-500">{t.statusLabel} — fora de veículo.</p>}
            <p className="text-sm font-semibold text-gray-900 pt-2">Condições atuais</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-surface-border p-3"><Info label="Sulco">{fmtMm(t.treadMm)}</Info></div>
              <div className="rounded-lg border border-surface-border p-3"><Info label="Pressão">{fmtPsi(t.pressurePsi)}</Info></div>
            </div>
            {vidaPct != null && (
              <div>
                <div className="flex justify-between text-xs text-gray-500"><span>Vida útil estimada (pelo sulco)</span><span className="font-medium text-gray-800">{Math.round(vidaPct * 100)}%</span></div>
                <div className="mt-1 h-2 rounded-full bg-surface-muted overflow-hidden">
                  <div className={`h-full rounded-full ${vidaPct < 0.15 ? "bg-danger-500" : vidaPct < 0.35 ? "bg-warning-500" : "bg-tm-500"}`} style={{ width: `${vidaPct * 100}%` }} />
                </div>
                <p className="text-[11px] text-gray-400 mt-1">De {fmtMm(t.mmNew)} (novo) até o limite de {fmtMm(t.mmSafety)}.</p>
              </div>
            )}
          </div>
          <div className="card p-5 space-y-3">
            <p className="text-sm font-semibold text-gray-900">Problemas recentes</p>
            {problemas.length === 0 ? <p className="text-sm text-gray-500">Nenhum problema registrado nas inspeções do SIAG.</p> : (
              <ul className="space-y-2">
                {problemas.slice(0, 5).map((i) => (
                  <li key={i.id} className="text-sm">
                    <div className="flex items-center gap-2"><ConditionBadge condition={i.condition} /><span className="text-xs text-gray-400">{fmtDate(i.inspectedAt)}</span></div>
                    <p className="text-gray-700 mt-0.5">{i.problems.map((p) => p.label).join(", ") || i.notes || "—"}</p>
                    {i.workOrderId && <Link className="text-xs text-tm-700 hover:underline" to={`/frota/manutencao/ordens/${i.workOrderId}`}>Ver O.S.</Link>}
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-2 border-t border-surface-border grid grid-cols-2 gap-3">
              <Info label="Última inspeção">{fmtDate(t.lastInspectionAt)}</Info>
              <Info label="Instalações no SIAG">{d.installations.length}</Info>
            </div>
          </div>
        </div>
      )}

      {(aba === "historico" || aba === "movimentacoes") && (
        <div className="card p-5">
          {hist.isLoading ? <p className="text-sm text-gray-500">Carregando…</p> : (
            <Timeline items={(hist.data?.items ?? []).filter((i) => aba === "historico" || i.kind !== "inspection")} />
          )}
        </div>
      )}

      {aba === "inspecoes" && (
        <div className="card overflow-x-auto">
          {d.inspections.length === 0 ? <EmptyState compact title="Nenhuma inspeção registrada" /> : (
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
                <tr><th className="text-left px-4 py-2">Data</th><th className="text-left px-4 py-2">Veículo / posição</th><th className="text-right px-4 py-2">Sulco</th><th className="text-right px-4 py-2">Pressão</th><th className="text-left px-4 py-2">Condição</th><th className="text-left px-4 py-2">Problemas</th><th className="text-left px-4 py-2">Por</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {d.inspections.map((i) => (
                  <tr key={i.id}>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDateTime(i.inspectedAt)}</td>
                    <td className="px-4 py-3 text-gray-600">{i.plate ?? "—"}{i.positionLabel ? ` · ${i.positionLabel}` : ""}</td>
                    <td className="px-4 py-3 text-right">{fmtMm(i.treadDepthMm)}</td>
                    <td className="px-4 py-3 text-right">{fmtPsi(i.pressurePsi)}</td>
                    <td className="px-4 py-3"><ConditionBadge condition={i.condition} /></td>
                    <td className="px-4 py-3 text-gray-600">{i.problems.map((p) => p.label).join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-gray-500">{i.inspectedBy ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {aba === "recapagens" && (
        <div className="card overflow-x-auto">
          {d.retreads.length === 0 ? <EmptyState compact title="Nenhuma recapagem registrada" description={d.retreadCount.integration > 0 ? `A integração informa ${d.retreadCount.integration} recapagem(ns) anterior(es) ao controle no SIAG — veja o Histórico.` : undefined} /> : (
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
                <tr><th className="text-left px-4 py-2">Nº</th><th className="text-left px-4 py-2">Reformador</th><th className="text-left px-4 py-2">Envio</th><th className="text-left px-4 py-2">Retorno</th><th className="text-left px-4 py-2">Banda</th><th className="text-right px-4 py-2">Custo</th><th className="text-left px-4 py-2">Situação</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {d.retreads.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3">R{r.sequence}</td>
                    <td className="px-4 py-3">{r.supplierName ?? "—"} <OriginBadge origin={r.source} /></td>
                    <td className="px-4 py-3">{fmtDate(r.sentAt)}{r.sentOdometerKm != null && <span className="block text-xs text-gray-400">{fmtKm(r.sentOdometerKm)}</span>}</td>
                    <td className="px-4 py-3">{fmtDate(r.returnedAt)}</td>
                    <td className="px-4 py-3">{r.treadPattern ?? "—"}</td>
                    <td className="px-4 py-3 text-right">{r.cost != null ? fmtBRL(r.cost) : "—"}</td>
                    <td className="px-4 py-3"><span className={RETREAD_STATUS[r.status].badge}>{RETREAD_STATUS[r.status].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {aba === "custos" && (
        <div className="card p-5 max-w-xl space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Compra</span><span>{fmtBRL(d.costs.purchase)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Recapagens (integração)</span><span>{fmtBRL(d.costs.retreadsIntegration)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Recapagens (registradas no SIAG)</span><span>{fmtBRL(d.costs.retreadsSiag)}</span></div>
          <div className="flex justify-between border-t border-surface-border pt-2 font-semibold"><span>Total</span><span>{fmtBRL(d.costs.total)}</span></div>
          <div className="flex justify-between text-gray-600">
            <span>Custo por km</span>
            <span>{d.costs.costPerKm != null ? `R$ ${d.costs.costPerKm.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}` : "—"}</span>
          </div>
          <p className="text-xs text-gray-400 pt-2">
            Base de km: {fmtKm(d.costs.kmBasis)} {d.km.accumulated ? "(km acumulado informado pela integração)" : "(km rodado nas instalações do SIAG)"}.
            Recapagem importada da Finatto não é somada duas vezes: o custo dela já vem dos eventos de recapagem da integração.
          </p>
        </div>
      )}

      {mover && <MoveTireModal preset={mover} onClose={() => setMover(null)} />}
      {inspecionar && <InspectTireModal tireId={t.id} tireCode={t.code} plate={loc?.plate} positionLabel={loc?.positionLabel} onClose={() => setInspecionar(false)} />}
    </div>
  );
}

function Timeline({ items }: { items: TireTimelineItemDto[] }) {
  if (items.length === 0) return <EmptyState compact title="Sem histórico ainda" description="Movimentações, inspeções e eventos da integração aparecem aqui." />;
  const dot: Record<TireTimelineItemDto["kind"], string> = { movement: "bg-tm-500", inspection: "bg-info-500", external: "bg-gray-300" };
  return (
    <ol className="relative border-l border-surface-border ml-2 space-y-5">
      {items.map((i, k) => (
        <li key={k} className="ml-5">
          <span className={`absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full ${dot[i.kind]}`} aria-hidden />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(i.at)}</span>
            <span className="text-sm font-medium text-gray-900">{i.title}</span>
            <OriginBadge origin={i.origin} />
          </div>
          <p className="text-sm text-gray-600 mt-0.5">
            {[i.plate, i.positionLabel].filter(Boolean).join(" · ")}
            {i.km != null ? `${i.plate || i.positionLabel ? " · " : ""}${fmtKm(i.km)}` : ""}
          </p>
          {i.detail && <p className="text-xs text-gray-500 mt-0.5">{i.detail}</p>}
          <p className="text-[11px] text-gray-400">
            {i.by ? `por ${i.by}` : ""}
            {i.workOrderId && <> {i.by ? "· " : ""}<Link className="text-tm-700 hover:underline" to={`/frota/manutencao/ordens/${i.workOrderId}`}>ver O.S.</Link></>}
          </p>
        </li>
      ))}
    </ol>
  );
}
